<template>
  <div>
    <organism-server-management
      v-if="filterData"
      :filter-list="filterData"
      :currentVeeamId="currentServerVeeam.id"
      :fields="fields"
      :items="serverList"
      :credentialList="credentialList"
      :serverBindingIpList="serverBindingIpList"
      @onUpdateFilterData="onUpdateFilterData"
      @onUpdateCurrentPage="onUpdateCurrentPage"
      @onSetDefaultFilterData="onSetDefaultFilterData"
      @addCredential="addCredentialToApi"
      @addServer="addServerToApi"
      @editServer="editServerToApi"
      @deleteServer="deleteServerToApi"
    ></organism-server-management>
  </div>
</template>

<script>
import {
  getVeeamCredential,
  createVeeamCredential,
  getVeeamServer,
  addVeeamServer,
  editVeeamServer,
  deleteVeeamServer,
  getRepositoryList,
  getInventoryBrowsers,
  getJobList,
} from "@/api/api-module3"
import mixins from "@/mixins"
import mixinsValidator from "@/mixins/mixinsValidator"
import { mapActions, mapGetters } from "vuex"
import OrganismServerManagement from "@/components/organisms/backupManagement/OrganismServerManagement.vue"
import mixinsObjectHelper from "@/mixins/mixinsObjectHelper"

export default {
  name: "ServerManagement",
  components: { OrganismServerManagement },
  mixins: [mixins, mixinsValidator, mixinsObjectHelper],
  data() {
    return {
      fields: [
        {
          key: "ip",
          label: "IP ADDRESS / HOST NAME",
          thClass: "w-25px",
          tdClass: "w-25px",
        },
        {
          key: "type",
          label: "TYPE",
          thClass: "w-25px",
          tdClass: "w-25px",
        },
        {
          key: "description",
          label: "DESCRIPTION",
          thClass: "w-300px",
          tdClass: "w-300px",
        },
        {
          key: "deleteIcon",
          label: "",
          thClass: "w-150px",
          tdClass: "w-150px",
        },
      ],
      serverList: [],
      credentialList: [],

      serverBindingIpList: [],
      filterData: null,
    }
  },
  computed: {
    ...mapGetters("app", ["getCurrentServerVeeam"]),
    ...mapGetters("appFilter", ["getBMMServerManagementFilter"]),

    currentServerVeeam() {
      return this.getCurrentServerVeeam
    },
  },
  watch: {
    currentPage() {
      this.getServerListFromApi()
    },
    currentServerVeeam() {
      if (this.hasString(this.currentServerVeeam.id)) {
        this.getCredentialListFromApi()
        this.getDataFromApi()
      }
    },
  },
  async mounted() {
    this.cloneFilterDataFromVuex()
    this.setShowVeeam(true)
    if (this.hasString(this.currentServerVeeam.id)) {
      await this.getCredentialListFromApi()
      await this.getDataFromApi()
    }
  },
  beforeDestroy() {
    this.setShowVeeam(false)
  },
  methods: {
    ...mapActions("app", ["setShowVeeam"]),
    ...mapActions("appFilter", ["setBMMServerManagementFilter"]),

    cloneFilterDataFromVuex() {
      const filterDataFromVuex = this.getBMMServerManagementFilter
      this.filterData = this.deepCopyObject(filterDataFromVuex)
    },

    async getDataFromApi() {
      await Promise.all([
        this.getServerListFromApi(),
        this.getServerBindingIpListFromApi(),
      ])
    },

    async getCredentialListFromApi() {
      this.showLoading(true)
      this.credentialList = []
      const baseFilter = { veeamId: this.currentServerVeeam.id }
      await getVeeamCredential(baseFilter)
        .then((response) => {
          if (response) {
            response.data.forEach((credential) => {
              this.credentialList.push({
                id: credential.id,
                username: credential.username,
                type: credential.type,
                description: credential.description,
                label: `${credential.username} (${credential.description})`,
              })
            })
          }
        })
        .finally(() => this.showLoading(false))
    },

    async addCredentialToApi(addCredential) {
      this.showLoading(true)
      await createVeeamCredential(addCredential, this.currentServerVeeam.id)
        .then(async (create) => {
          if (create.status === "ok") {
            this.toastSuccess("Created successfully")
            await this.getCredentialListFromApi()
          } else {
            this.toastError("Created failed")
          }
        })
        .catch(() => {
          this.toastError("Created failed")
        })
        .finally(() => this.showLoading(false))
    },

    async getServerListFromApi() {
      this.showLoading(true)
      this.cloneFilterDataFromVuex()
      const filterData = this.filterDecoration()
      await getVeeamServer(filterData)
        .then((response) => {
          if (response) {
            this.filterData.totalCount = response.pagination.total
            const updatedFilter = {
              ...this.getBMMServerManagementFilter,
              totalCount: response.pagination.total,
            }
            this.setBMMServerManagementFilter(updatedFilter)
            this.serverList = response.data.map((server) => {
              return {
                id: server.id,
                ip: server.name,
                type: this.serverTypeDecoration(server.type),
                credentialsId: server.credentialsId,
                description: server.description,
              }
            })
          }
        })
        .finally(() => this.showLoading(false))
    },

    serverTypeDecoration(type) {
      let serverType = ""
      if (type === "ViHost") serverType = "Virtual Machine"
      else if (type === "WindowsHost") serverType = "Windows"
      else if (type === "LinuxHost") serverType = "Linux"
      else serverType = "undefined"
      return serverType
    },

    filterDecoration() {
      const filter = {
        Page: this.filterData.currentPage,
        nameFilter: this.filterData.ip ? `*${this.filterData.ip}*` : undefined,
        typeFilter: this.mapTypeToApiFormat(this.filterData.type),
        veeamId: this.currentServerVeeam.id,
      }
      return filter
    },

    mapTypeToApiFormat(type) {
      if (type == "Virtual Machine") return "ViHost"
      else if (type === "Windows") return "WindowsHost"
      else if (type === "Linux") return "LinuxHost"
      else return undefined
    },

    // click filter
    onUpdateFilterData(filter) {
      filter.currentPage = 1
      this.setBMMServerManagementFilter(filter)
      this.getServerListFromApi()
    },

    onUpdateCurrentPage(filter) {
      const updatedFilter = {
        ...this.getBMMServerManagementFilter,
        currentPage: filter.currentPage,
      }
      this.setBMMServerManagementFilter(updatedFilter)
      this.getServerListFromApi()
    },

    onSetDefaultFilterData() {
      const defaultFilter = {
        ip: "",
        type: "",
        currentPage: 1,
        totalCount: 0,
      }
      this.setBMMServerManagementFilter(defaultFilter)
      this.getServerListFromApi()
    },

    async getServerBindingIpListFromApi() {
      this.showLoading(true)
      this.serverBindingIpList = []

      await getRepositoryList({ veeamId: this.currentServerVeeam.id }).then(
        (response) => {
          if (response) {
            response.data.forEach((server) => {
              if (!this.serverBindingIpList.includes(server.hostname))
                this.serverBindingIpList.push(server.hostname)
            })
          }
        }
      )

      await getInventoryBrowsers({ veeamId: this.currentServerVeeam.id }).then(
        (response) => {
          if (response) {
            response.data.data.forEach((server) => {
              if (
                !this.serverBindingIpList.includes(
                  server.inventoryObject.hostName
                )
              )
                this.serverBindingIpList.push(server.inventoryObject.hostName)
            })
          }
        }
      )

      await getJobList({ veeamId: this.currentServerVeeam.id })
        .then((response) => {
          if (response) {
            response.data.data.forEach((job) => {
              job.virtualMachines.includes.forEach((vm) => {
                if (
                  !this.serverBindingIpList.includes(
                    vm.inventoryObject.hostName
                  )
                )
                  this.serverBindingIpList.push(vm.inventoryObject.hostName)
              })
            })
          }
        })
        .finally(() => this.showLoading(false))
    },

    async addServerToApi(request) {
      this.showLoading(true)
      const duplicateName = this.serverList.find((server) => {
        return server.ip === request.name
      })
      if (duplicateName) {
        this.toastError(`${duplicateName.ip} is already use`)
      } else {
        await addVeeamServer(request, this.currentServerVeeam.id)
          .then(async (response) => {
            if (response.status === "ok") {
              this.showLoading(true)

              let interval = window.setInterval(() => {
                const filterData = this.filterDecoration()
                getVeeamServer(filterData).then((res) => {
                  if (res) {
                    if (
                      res.data.find((server) => server.name === request.name)
                    ) {
                      window.clearInterval(interval)
                      this.toastSuccess("Add server successfully")
                      this.getDataFromApi()
                      this.showLoading(false)
                    }
                  }
                })
              }, 500)
            } else {
              this.showLoading(false)
              this.toastError("Invalid Credential")
            }
          })
          .catch(() => {
            this.showLoading(false)
            this.toastError("Invalid Credential")
          })
      }
    },

    async editServerToApi(request) {
      this.showLoading(true)
      await editVeeamServer(request.id, request, this.currentServerVeeam.id)
        .then((response) => {
          if (response.status === "ok") {
            this.showLoading(true)
            setTimeout(() => {
              this.showLoading(false)
              this.toastSuccess("Edit server successfully")
              this.getDataFromApi()
            }, 15000)
          } else {
            this.toastError("Invalid Credential")
          }
        })
        .catch(() => {
          this.toastError("Invalid Credential")
        })
    },

    async deleteServerToApi(request) {
      this.showLoading(true)
      await deleteVeeamServer(request, this.currentServerVeeam.id)
        .then((response) => {
          if (response.status === "ok") {
            this.showLoading(true)
            setTimeout(() => {
              this.showLoading(false)
              this.toastSuccess("Delete successfully")
              this.getDataFromApi()
            }, 15000)
          } else {
            this.toastError(
              "Delete failed, Please check credential before delete"
            )
          }
        })
        .catch(() => {
          this.toastError(
            "Delete failed, Please check credential before delete"
          )
        })
    },
  },
}
</script>

<style lang="scss" scoped></style>
